import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "@/components/ui/table";
import { 
  Settings, Calendar, School, FileText, Upload, Plus, Download, Edit, Eye
} from 'lucide-react';

const cycles = [
  { name: 'CDS Pilot C1', year: 2026, curation: 'N/A', selection: 'N/A', delivery: 'Aug–Oct 2026', schools: 5, status: 'complete' },
  { name: 'CDS Pilot C2', year: 2026, curation: 'N/A', selection: 'N/A', delivery: 'Nov 2026, Jan–Feb 2027', schools: 5, status: 'in_progress' },
  { name: 'CDS Cycle 1 2028', year: 2028, curation: '30 Apr 2026', selection: '15 Aug–15 Sep 2026', delivery: 'Jan–May 2028', schools: 287, status: 'curation' },
  { name: 'CDS Cycle 2 2028', year: 2028, curation: '30 Nov 2026', selection: 'TBD', delivery: 'Jun–Oct 2028', schools: 287, status: 'upcoming' },
];

const schools = [
  { name: 'Raffles Girls\' School', level: 'Secondary', cluster: 'Central', budget: '$3,000', coordinator: 'Mrs Tan Mei Ling', status: 'active' },
  { name: 'ACS (Independent)', level: 'Secondary', cluster: 'Central', budget: '$3,000', coordinator: 'Mr Bernard Chng', status: 'active' },
  { name: 'Nanyang Primary', level: 'Primary', cluster: 'West', budget: '$3,000', coordinator: 'Mr Loh Beng Huat', status: 'active' },
  { name: 'Victoria Junior College', level: 'Pre-U', cluster: 'Central', budget: '$3,000', coordinator: 'Ms Priscilla Koh', status: 'active' },
  { name: 'Queenstown Secondary', level: 'Secondary', cluster: 'Central', budget: '$3,000', coordinator: 'Mrs Fatimah Yusof', status: 'active' },
];

const getStatusBadge = (status) => {
  const statusConfig = {
    complete: { label: 'Complete', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    in_progress: { label: 'In Progress', className: 'bg-amber-50 text-amber-700 border-amber-200' },
    curation: { label: 'Curation Phase', className: 'bg-blue-50 text-blue-700 border-blue-200' },
    upcoming: { label: 'Upcoming', className: 'bg-slate-50 text-slate-700 border-slate-200' },
    active: { label: 'Active', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  };
  const config = statusConfig[status] || statusConfig.upcoming;
  return <Badge variant="outline" className={config.className}>{config.label}</Badge>;
};

export default function MOECycleSetup() {
  const [activeTab, setActiveTab] = useState('active');

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-3">
            <Settings className="w-7 h-7 text-[#1A3C6E]" />
            CDS Cycle Setup & Administration
          </h1>
          <p className="text-slate-500 mt-1">Configure CDS cycles, manage schools, and track programme settings</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="active" data-testid="tab-active-cycles">Active Cycles</TabsTrigger>
          <TabsTrigger value="create" data-testid="tab-create-cycle">Create New Cycle</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-cycle-history">Cycle History</TabsTrigger>
          <TabsTrigger value="schools" data-testid="tab-school-assignments">School Assignments</TabsTrigger>
        </TabsList>

        {/* Active Cycles Tab */}
        <TabsContent value="active" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Current CDS Cycles</CardTitle>
              <CardDescription>View and manage active and upcoming cycles</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cycle</TableHead>
                    <TableHead>Year</TableHead>
                    <TableHead>Curation Deadline</TableHead>
                    <TableHead>Selection Window</TableHead>
                    <TableHead>Delivery Period</TableHead>
                    <TableHead>Schools</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cycles.map((cycle, idx) => (
                    <TableRow key={idx} className="table-row-striped">
                      <TableCell className="font-medium">{cycle.name}</TableCell>
                      <TableCell>{cycle.year}</TableCell>
                      <TableCell>{cycle.curation}</TableCell>
                      <TableCell>{cycle.selection}</TableCell>
                      <TableCell>{cycle.delivery}</TableCell>
                      <TableCell>{cycle.schools}</TableCell>
                      <TableCell>{getStatusBadge(cycle.status)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm">
                            <Eye className="w-4 h-4" />
                          </Button>
                          {cycle.status !== 'complete' && (
                            <Button variant="ghost" size="sm">
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Create New Cycle Tab */}
        <TabsContent value="create" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Create New CDS Cycle</CardTitle>
              <CardDescription>Configure a new cycle with key dates and parameters</CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              {/* Section A - Cycle Identity */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-[#1A3C6E] text-white text-xs flex items-center justify-center">A</span>
                  Cycle Identity
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="cycleName">Cycle Name</Label>
                    <Input id="cycleName" placeholder="CDS Cycle ___ 20___" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="deliveryYear">Year of Delivery</Label>
                    <Input id="deliveryYear" placeholder="2028" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="cycleNumber">Cycle</Label>
                    <Input id="cycleNumber" placeholder="1 or 2" className="mt-1" />
                  </div>
                </div>
              </div>

              {/* Section B - Key Dates */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-[#1A3C6E] text-white text-xs flex items-center justify-center">B</span>
                  Key Dates
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="curationDeadline">Curation Submission Deadline</Label>
                    <Input id="curationDeadline" type="date" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="quoteDeadline">MOE HQ Quote Approval Deadline</Label>
                    <Input id="quoteDeadline" type="date" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="selectionStart">School Selection Opens</Label>
                    <Input id="selectionStart" type="date" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="selectionEnd">School Selection Deadline</Label>
                    <Input id="selectionEnd" type="date" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="deliveryStart">Target First Delivery Month</Label>
                    <Input id="deliveryStart" type="month" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="deliveryEnd">Target Last Delivery Month</Label>
                    <Input id="deliveryEnd" type="month" className="mt-1" />
                  </div>
                </div>
              </div>

              {/* Section C - Title Quotas */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-[#1A3C6E] text-white text-xs flex items-center justify-center">C</span>
                  Title Quotas (per RFP Table 2)
                </h3>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Category</TableHead>
                        <TableHead>Primary</TableHead>
                        <TableHead>Secondary</TableHead>
                        <TableHead>Pre-University</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell>Non-Fiction EL</TableCell>
                        <TableCell><Input defaultValue="25" className="w-20" /></TableCell>
                        <TableCell><Input defaultValue="30" className="w-20" /></TableCell>
                        <TableCell><Input defaultValue="50" className="w-20" /></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>MTL (CL)</TableCell>
                        <TableCell><Input defaultValue="35" className="w-20" /></TableCell>
                        <TableCell><Input defaultValue="30" className="w-20" /></TableCell>
                        <TableCell><Input defaultValue="16" className="w-20" /></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>MTL (ML)</TableCell>
                        <TableCell><Input defaultValue="15" className="w-20" /></TableCell>
                        <TableCell><Input defaultValue="13" className="w-20" /></TableCell>
                        <TableCell><Input defaultValue="4" className="w-20" /></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>MTL (TL)</TableCell>
                        <TableCell><Input defaultValue="5" className="w-20" /></TableCell>
                        <TableCell><Input defaultValue="5" className="w-20" /></TableCell>
                        <TableCell><Input defaultValue="3" className="w-20" /></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Fiction EL</TableCell>
                        <TableCell><Input defaultValue="60" className="w-20" /></TableCell>
                        <TableCell><Input defaultValue="60" className="w-20" /></TableCell>
                        <TableCell><Input defaultValue="60" className="w-20" /></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Section D - Budget */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-[#1A3C6E] text-white text-xs flex items-center justify-center">D</span>
                  Budget
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="schoolBudget">Per School Annual Budget</Label>
                    <Input id="schoolBudget" defaultValue="$3,000" className="mt-1" />
                    <p className="text-xs text-slate-500 mt-1">Inflation auto-applies at 3%/yr</p>
                  </div>
                  <div>
                    <Label htmlFor="mkBudget">MK Annual Budget (per kindergarten)</Label>
                    <Input id="mkBudget" defaultValue="$550" className="mt-1" />
                  </div>
                </div>
              </div>

              {/* Section E - Pilot Flag */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-[#1A3C6E] text-white text-xs flex items-center justify-center">E</span>
                  Pilot Flag
                </h3>
                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                  <input type="checkbox" id="isPilot" className="w-4 h-4" />
                  <Label htmlFor="isPilot" className="font-normal">
                    This is a Pilot cycle — pilot schools upload list, titles provided by MOE HQ, invoice to MOE HQ not schools
                  </Label>
                </div>
              </div>

              {/* Section F - e-AOR */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-[#1A3C6E] text-white text-xs flex items-center justify-center">F</span>
                  e-AOR
                </h3>
                <div className="flex items-center gap-4">
                  <Button variant="outline">
                    <Upload className="w-4 h-4 mr-2" />
                    Upload High-level e-AOR (PDF)
                  </Button>
                  <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">Pending</Badge>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-4 pt-4 border-t">
                <Button variant="outline">
                  <FileText className="w-4 h-4 mr-2" />
                  Save Draft
                </Button>
                <Button className="bg-[#1A3C6E] hover:bg-[#142E54]" data-testid="create-cycle-btn">
                  Create Cycle & Notify Civica
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cycle History Tab */}
        <TabsContent value="history" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Cycle History</CardTitle>
              <CardDescription>Past CDS cycles and their outcomes</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-slate-500 text-center py-8">No completed cycles to display yet.</p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* School Assignments Tab */}
        <TabsContent value="schools" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading">School Assignments</CardTitle>
                <CardDescription>287 schools assigned to CDS programme</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
                <Button variant="outline" size="sm">
                  <Upload className="w-4 h-4 mr-2" />
                  Import
                </Button>
                <Button size="sm" className="bg-[#1A3C6E] hover:bg-[#142E54]">
                  <Plus className="w-4 h-4 mr-2" />
                  Add School
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <Input placeholder="Search schools..." className="max-w-sm" />
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>School</TableHead>
                    <TableHead>Level</TableHead>
                    <TableHead>Cluster</TableHead>
                    <TableHead>Budget</TableHead>
                    <TableHead>Library Coordinator</TableHead>
                    <TableHead>Portal Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {schools.map((school, idx) => (
                    <TableRow key={idx} className="table-row-striped">
                      <TableCell className="font-medium">{school.name}</TableCell>
                      <TableCell>{school.level}</TableCell>
                      <TableCell>{school.cluster}</TableCell>
                      <TableCell>{school.budget}</TableCell>
                      <TableCell>{school.coordinator}</TableCell>
                      <TableCell>{getStatusBadge(school.status)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-sm text-slate-500 mt-4 text-center">Showing 5 of 287 schools</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
